import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { get, getDropDownvalues, uploadFile } from "../../../services/api";
import { toast } from "react-toastify";
import { updateCandidates } from "../../../store/actions/Candidates/candidateActions";
import { getFilenameFromLink } from "../../../utils/getFilenameFromLink";
import DatePicker from "react-datepicker";
import moment from "moment"; // Import moment for date formatting

const ComplianceCriteria = ({ selectedCandidate, token, isView, close }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [sastoken, setsastoken] = useState([]);
  const dispatch = useDispatch();
  const nav = useNavigate();
  
  useEffect(() => {
    getSasToken();
  }, []);

  const getSasToken = async () => {
    const response = await get('/api/document/generate-sas-token', token);
    setsastoken(response?.sasToken);
  };

  useEffect(() => {
    const getQuestions = async () => {
      try {
        setIsLoading(true);
        const listdata = await getDropDownvalues(
          "CANDIDATE_COMPLIANCE_CRITERIA_QUESTIONS",
          token
        );
        const questionList =
          listdata?.map((itm) => ({
            question: itm.value,
            yesNo: false,
            isTwentyHourRestriction: false,
            comment: "",
            issueDate: "",
            expireDate: "",
            fileUrl: "",
            secondFileUrl: "",
            rtwDocuments: [] // Initialize RTW documents array
          })) || [];
        
        if (selectedCandidate?.complianceCriteria?.length > 0) {
          const existingQuestions = selectedCandidate.complianceCriteria.map(criteria => {
            // Preserve existing documents for all question types
            const rtwDocuments = criteria.rtwDocuments || [];
            
            // Special handling for RTW-related questions
            if (criteria.question.toLowerCase().includes('rtw')) {
              // Combine documents from fileUrl and rtwDocuments
              const combinedDocuments = [...rtwDocuments];
              
              // If fileUrl exists and not already in rtwDocuments, add it
              if (criteria.fileUrl && !rtwDocuments.some(doc => doc.fileUrl === criteria.fileUrl)) {
                combinedDocuments.push({
                  fileUrl: criteria.fileUrl,
                  secondFileUrl: criteria.secondFileUrl,
                  uploadDate: new Date(), // Use current date if upload date is not available
                  issueDate: criteria.issueDate,
                  expireDate: criteria.expireDate,
                  status: criteria.expireDate && new Date(criteria.expireDate) < new Date() ? 'expired' : 'active',
                  comment: criteria.comment
                });
              }
              
              // Sort documents by upload date, most recent first
              combinedDocuments.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
              
              return {
                ...criteria,
                rtwDocuments: combinedDocuments
              };
            }
            
            // For non-RTW documents, create a document entry if fileUrl exists
            if (criteria.fileUrl) {
              const newDocument = {
                fileUrl: criteria.fileUrl,
                secondFileUrl: criteria.secondFileUrl,
                uploadDate: new Date(), // Use current date if upload date is not available
                issueDate: criteria.issueDate,
                expireDate: criteria.expireDate,
                status: criteria.expireDate && new Date(criteria.expireDate) < new Date() ? 'expired' : 'active',
                comment: criteria.comment
              };
              
              // Only add the document if rtwDocuments is empty
              if (rtwDocuments.length === 0) {
                rtwDocuments.push(newDocument);
              }
            }

            return {
              ...criteria,
              rtwDocuments: rtwDocuments
            };
          });
          
          const newQuestions = questionList.filter(
            (q) => !existingQuestions.some((eq) => eq.question === q.question)
          );
          
          setQuestions([...existingQuestions, ...newQuestions]);
        } else {
          setQuestions(questionList);
        }
      } catch (error) {
        toast.error("Failed to load questions.");
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    getQuestions();
  }, [selectedCandidate, token]);

  const handleInputChange = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index][field] = value;
    setQuestions(updatedQuestions);
  };

  const handleFileUpload = async (index, file, field) => {
    try {
      setIsLoading(true);
      const fileUrl = await uploadFile(file, token, selectedCandidate?._id);
      
      // Create a new document entry for all document types
      const newDocument = {
        fileUrl: fileUrl?.url,
        uploadDate: new Date(),
        issueDate: questions[index].issueDate,
        expireDate: questions[index].expireDate,
        status: questions[index].expireDate && new Date(questions[index].expireDate) < new Date() ? 'expired' : 'active',
        uploadedBy: 'current_user', // Replace with actual user info
        comment: questions[index].comment
      };

      // Update the questions state
      const updatedQuestions = [...questions];
      const currentQuestion = updatedQuestions[index];

      // Ensure rtwDocuments array exists
      if (!currentQuestion.rtwDocuments) {
        currentQuestion.rtwDocuments = [];
      }

      // Handle different file upload scenarios
      if (field === 'secondFileUrl') {
        // For second file, add to the last document or create a new one
        if (currentQuestion.rtwDocuments.length > 0) {
          currentQuestion.rtwDocuments[currentQuestion.rtwDocuments.length - 1].secondFileUrl = fileUrl?.url;
        } else {
          newDocument.secondFileUrl = fileUrl?.url;
          currentQuestion.rtwDocuments.push(newDocument);
        }
      } else {
        // For primary file, add a new document
        currentQuestion.rtwDocuments.push(newDocument);
      }

      // Update the main fileUrl and secondFileUrl for all documents
      currentQuestion.fileUrl = currentQuestion.rtwDocuments[currentQuestion.rtwDocuments.length - 1].fileUrl;
      if (field === 'secondFileUrl') {
        currentQuestion.secondFileUrl = fileUrl?.url;
      }

      // Sort documents by upload date, most recent first
      currentQuestion.rtwDocuments.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));

      setQuestions(updatedQuestions);
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error("File upload failed");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    // Validate RTW file upload
    const rtwQuestion = questions.find(q => q.question.toLowerCase().includes('rtw'));
    
    if (rtwQuestion && rtwQuestion.yesNo && (!rtwQuestion.rtwDocuments || rtwQuestion.rtwDocuments.length === 0)) {
      toast.warning("Please upload the RTW check file before saving");
      return;
    }
    
    // Validate Enrollment Letter file upload
    const enrollmentLetterQuestion = questions.find(q => q.question.toLowerCase().includes('enrolment letter') || q.question.toLowerCase().includes('enrollment letter'));
    
    if (enrollmentLetterQuestion && enrollmentLetterQuestion.yesNo && !enrollmentLetterQuestion.fileUrl) {
      toast.warning("Please upload the Enrollment Letter file before saving");
      return;
    }
    
    const payload = {
      complianceCriteria: questions.map((q) => {
        // For non-RTW questions, preserve the first document if it exists
        if (!q.question.toLowerCase().includes('rtw') && q.rtwDocuments && q.rtwDocuments.length > 0) {
          q.fileUrl = q.rtwDocuments[0].fileUrl;
          q.secondFileUrl = q.rtwDocuments[0].secondFileUrl;
          q.issueDate = q.rtwDocuments[0].issueDate;
          q.expireDate = q.rtwDocuments[0].expireDate;
          q.comment = q.rtwDocuments[0].comment;
        }
        
        return {
          ...q,
          yesNo: Boolean(q.yesNo),
          isTwentyHourRestriction: Boolean(q.isTwentyHourRestriction),
        };
      }),
    };
    
    dispatch(
      updateCandidates(payload, () => {
        toast.success("Compliance criteria saved successfully");
      }, selectedCandidate?._id, token)
    );
  };

  const handleFileDelete = (index, documentIndex) => {
    const updatedQuestions = [...questions];
    // Remove the specific RTW document
    updatedQuestions[index].rtwDocuments.splice(documentIndex, 1);
    setQuestions(updatedQuestions);
    toast.success("File deleted successfully");
  };

  return (
    <div className="container my-4 compliance-criteria">
      {isLoading ? (
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <ul className="list-group">
          {questions.map((item, index) => (
            <li key={index} className="list-group-item mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{item.question}</h5>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={item.yesNo}
                    disabled={isView}
                    onChange={(e) =>
                      handleInputChange(index, "yesNo", e.target.checked)
                    }
                  />
                  <label className="form-check-label">
                    {item.yesNo ? "Yes" : "No"}
                  </label>
                </div>
              </div>

              {item.yesNo && (
                <div className="mt-3">
                  <div className="mb-3">
                    <label className="form-label">Comment</label>
                    <input
                      type="text"
                      className="form-control"
                      value={item.comment}
                      disabled={isView}
                      onChange={(e) =>
                        handleInputChange(index, "comment", e.target.value)
                      }
                    />
                  </div>

                  <div
                    className="row"
                    style={{ display: item.question?.toLowerCase()?.includes("ni") ? "none" : "" }}
                  >
                    <div className="col-md-6">
                      <label className="form-label">Issue Date</label>
                      <DatePicker
                        selected={item.issueDate ? new Date(item.issueDate) : null}
                        onChange={(date) => handleInputChange(index, "issueDate", date)}
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                        disabled={isView}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Expiry Date</label>
                      <DatePicker
                        selected={item.expireDate ? new Date(item.expireDate) : null}
                        onChange={(date) => handleInputChange(index, "expireDate", date)}
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                        disabled={isView}
                      />
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className="col-md-12 mt-3">
                          <label className="form-label">Upload File</label>
                          <input
                            type="file"
                            className="form-control"
                            disabled={isView}
                            onChange={(e) =>
                              handleFileUpload(index, e.target.files[0], "fileUrl")
                            }
                          />
                        </div>

                         {/* Optional: Second File Upload for BRP/Passport */}
                  {(item.question?.toLowerCase()?.includes("brp") || item.question?.toLowerCase()?.includes("passport")) && (
                    <div className="col-md-12 mt-3">
                      <label className="form-label">Upload Second File</label>
                      <input
                        type="file"
                        className="form-control"
                        disabled={isView}
                        onChange={(e) =>
                          handleFileUpload(index, e.target.files[0], "secondFileUrl")
                        }
                      />
                    </div>
                  )}
                  
                  {/* Document Upload and History Section */}
                  {(item.fileUrl || (item.rtwDocuments && item.rtwDocuments.length > 0)) && (
                    <div className="row mt-3">
                      
                      <div className="col-md-12 mt-3">
                        <h6>Document History</h6>
                        <table className="table table-striped table-bordered">
                          <thead>
                            <tr>
                              <th>File Name</th>
                              <th>Secondary File</th>
                              <th>Status</th>
                              <th>Issue Date</th>
                              <th>Expiry Date</th>
                              <th>Comment</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.rtwDocuments.map((doc, documentIndex) => (
                              <tr key={documentIndex}>
                                <td>
                                  <a 
                                    href={doc.fileUrl + "?" + sastoken} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                  >
                                    {getFilenameFromLink(doc.fileUrl)}
                                  </a>
                                </td>
                                <td>
                                  {doc.secondFileUrl && (
                                    <a 
                                      href={doc.secondFileUrl + "?" + sastoken} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                    >
                                      {getFilenameFromLink(doc.secondFileUrl)}
                                    </a>
                                  )}
                                </td>
                                <td>
                                  {doc.status && (
                                    <span className={`badge ${doc.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                      {doc.status}
                                    </span>
                                  )}
                                </td>
                                <td>{doc.issueDate ? moment(doc.issueDate).format('DD/MM/YYYY') : ''}</td>
                                <td>{doc.expireDate ? moment(doc.expireDate).format('DD/MM/YYYY') : ''}</td>
                                <td>{doc.comment || ''}</td>
                                <td>
                                  <button 
                                    className="btn btn-sm btn-danger" 
                                    onClick={() => handleFileDelete(index, documentIndex)}
                                    disabled={isView}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        
                      </div>
                    </div>
                  )}
                  
                 
                  
                  {/* 20-hour Restriction Toggle for RTW */}
                  <div className="col-md-6 mt-3" style={{display: item.question?.toLowerCase()?.includes("rtw") ? "block" : "none"}}>
                    <div className="form-check form-switch">
                      <label className="form-check-label">20 hour restriction?</label>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={item.isTwentyHourRestriction}
                        disabled={isView}
                        onChange={(e) =>
                          handleInputChange(index, "isTwentyHourRestriction", e.target.checked)
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="d-flex justify-content-start mt-4">
        <button
          style={{ display: isView ? "none" : "" }}
          onClick={handleSave}
          className="btn btn-primary me-2"
        >
          Save
        </button>
        <button type="button" onClick={() => close()} className="btn btn-danger">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ComplianceCriteria;
